import {useDispatch, useSelector} from 'react-redux';
import axios from 'axios';
import {getHeaders} from '@core/actions/xhrRequest';
import {getUser} from '@core/actions/UserActions';
import {clearFiles} from '@form/actions/FileActions';
import {showOverlay, hideOverlay} from '../../common/Overlay';
import {burstConfetti} from '../../common/Confetti';
import {COUPON_STATUS_SUCCESS} from '../actions/CouponActions';
import {redirectViaWorldpayGate} from '@common/helpers/worldpayHppGate';

const STATUS_USER_BLOCKED = 423;
const STATUS_USER_EXISTS = 452;
const STATUS_USER_NOT_FOUND = 453;
const STATUS_INVALID_PASSWORD = 454;
const STATUS_FORBIDDEN_EMAIL = 455;
const STATUS_EMAIL_SUGGESTION = 456;

export default function useResumeSubmit({setStep, onSuccess} = {}) {
    const dispatch = useDispatch();
    const {user, authorized} = useSelector(state => state.user);
    const {data: options} = useSelector(state => state.resumeOptions);
    const files = useSelector(state => state.file.data);
    const coupon = useSelector(state => state.coupon);
    // Only treat the coupon as applicable when upstream validated it.
    // Anything in `state.coupon` with another status (PENDING/FAILURE/null)
    // must not leak into the submitted order.
    const couponApplied = coupon && coupon.status === COUPON_STATUS_SUCCESS && coupon.data && coupon.data.isValid
        ? coupon
        : null;

    const careerMapping = options?.careerLevelMapping || {};

    const signIn = async (email, password) => {
        try {
            await axios.post('/api/user/sign-in', JSON.stringify({email, password}), {headers: getHeaders()});
            dispatch(getUser());
            return null;
        } catch (error) {
            const status = error.response?.status;
            switch (status) {
                case STATUS_USER_NOT_FOUND:
                    return {email: "User doesn't exist"};
                case STATUS_INVALID_PASSWORD:
                    return {password: 'Password is incorrect'};
                default:
                    return {email: 'Sign in failed. Please try again.'};
            }
        }
    };

    const signUp = async (values) => {
        try {
            await axios.post('/api/user/sign-up', JSON.stringify({
                name: values.name,
                email: values.email,
                phone: values.phoneCode.replace('+', '') + ' ' + values.phoneNumber,
                country_iso: values.countryCodeFlag,
                subscribe: 0,
                user_time_offset: new Date().getTimezoneOffset() * -1 * 60,
            }), {headers: getHeaders()});
            dispatch(getUser());
            return null;
        } catch (error) {
            const status = error.response?.status;
            switch (status) {
                case STATUS_USER_EXISTS:
                    return {email: 'User exists'};
                case STATUS_USER_BLOCKED:
                    return {email: 'You have been blocked. Try again later.'};
                case STATUS_EMAIL_SUGGESTION:
                    return {email: 'Did you mean ' + (error.response?.data?.message || '') + '?'};
                case STATUS_FORBIDDEN_EMAIL:
                    return {email: 'Forbidden email domain.'};
                default:
                    return {email: 'Registration failed. Please try again.'};
            }
        }
    };

    const buildFormData = (values) => {
        const form = new FormData();

        const urgencyHours = options.urgency?.[values.urgency]?.hours || 0;

        form.append('order[academic_level]', careerMapping[values.careerLevel] || '');
        form.append('order[deadline]', urgencyHours * 3600);
        form.append('order[form_type]', window.location.pathname.includes('/account/') ? 'form_inner' : 'form_outer');

        // Coupon — only forward when upstream actually validated it.
        // Sanitize to alphanumerics on the wire to mirror what the
        // legacy Payment.php class does anyway (defence in depth).
        const validCouponCode = couponApplied?.data?.name || couponApplied?.code || '';
        if (validCouponCode) {
            form.append('order[coupon]', String(validCouponCode).replace(/[^a-zA-Z0-9]/g, ''));
        }

        // Toggle to apply customer's store credit on this order. BFF reads
        // it at the top level (not under `order[...]`) and chains the
        // upstream apply-balance call after order creation.
        if (values.useBalance) {
            form.append('use_balance', '1');
        }

        (values.services || []).forEach((id, i) => {
            form.append(`order[service_ids][${i}]`, id);
        });
        (values.features || []).forEach((id, i) => {
            form.append(`order[feature_ids][${i}]`, id);
        });
        const validFiles = files.filter(f => f.status && !f.uploading);
        validFiles.forEach((file, i) => {
            form.append(`files[${i}][name]`, file.name);
            form.append(`files[${i}][originalName]`, file.originalName);
            form.append(`files[${i}][size]`, file.size);
            form.append(`files[${i}][mimeType]`, file.mimeType || '');
        });

        return form;
    };

    const submitOrder = (values) => {
        showOverlay();

        axios.post('/api/orders', buildFormData(values), {headers: getHeaders(), timeout: 60000})
            .then(response => {
                dispatch(clearFiles());
                if (setStep) setStep(0);
                burstConfetti({count: 140});
                const target = response.data.url || response.data.redirectUrl;
                setTimeout(() => {
                    const isExternal = target && (target.startsWith('http://') || target.startsWith('https://'));
                    if (isExternal) {
                        redirectViaWorldpayGate(target, {
                            headers: getHeaders(),
                            onFailure: () => {
                                hideOverlay();
                                location.reload();
                            },
                        });
                    } else if (onSuccess) {
                        onSuccess(response.data);
                    } else if (target) {
                        location.href = target;
                    } else {
                        location.reload();
                    }
                }, 650);
            })
            .catch(error => {
                hideOverlay();
                if (error.response?.data?.redirectTo) {
                    location.href = error.response.data.redirectTo;
                }
            });
    };

    return {signIn, signUp, submitOrder, user, authorized};
}
